import { ApiClient, type ApiClientOptions } from '@/infra/api-client'
import {
  type DraftPayoutResource,
  type ListPendingTransactionsRequest,
  type PendingTransactionResource,
  type ApproveTransactionsRequest,
  type ApproveTransactionsResponse,
  type RemoveTransactionsRequest,
  type RemoveTransactionsResponse,
  type SubmitDraftPayoutRequest,
  type SubmitDraftPayoutResponse,
} from '@/features/payouts/api/resources/drafting'
import type { AipCursorPaged } from '@/infra/paging'
import {
  type AdminTransactionResource,
  type CreateUserTransactionRequest,
  type CreateUserTransactionResponse,
  type CancelUserTransactionResponse,
  type ListAdminHistoricalTransactionsRequest,
  type AdminHistoricalTransactionResource,
  type DownloadAdminHistoricalTransactionsAsCsvRequest,
} from '@/features/payouts/api/resources/admin-transactions'
import { type PayoutResource } from '@/features/payouts/api/resources/payouts'
import { toURLSearchParams } from '@/utils/url-util'

/**
 * Admin payouts endpoints of the BigMoney API.
 */
export class PayoutsAdminApi {
  readonly draftPayouts
  readonly pendingTransactions
  readonly transactions
  readonly payouts

  constructor(options: ApiClientOptions) {
    this.draftPayouts = new DraftPayoutApi(options)
    this.pendingTransactions = new PendingTransactionApi(options)
    this.transactions = new AdminTransactionApi(options)
    this.payouts = new AdminPayoutApi(options)
  }
}

/**
 * API for working with draft payouts.
 */
export class DraftPayoutApi extends ApiClient {
  getCurrent(signal?: AbortSignal): Promise<DraftPayoutResource> {
    return this._get(`payouts/admin/v1/draft-payouts/current`, undefined, {
      signal,
    })
  }

  approveTransactions(
    id: string,
    request: ApproveTransactionsRequest,
  ): Promise<ApproveTransactionsResponse> {
    return this._post(
      `payouts/admin/v1/draft-payouts/${id}/approve-transactions`,
      request,
    )
  }

  removeTransactions(
    id: string,
    request: RemoveTransactionsRequest,
  ): Promise<RemoveTransactionsResponse> {
    return this._post(
      `payouts/admin/v1/draft-payouts/${id}/remove-transactions`,
      request,
    )
  }

  submitPayout(
    id: string,
    request: SubmitDraftPayoutRequest,
  ): Promise<SubmitDraftPayoutResponse> {
    return this._post(`payouts/admin/v1/draft-payouts/${id}/submit`, request)
  }
}

/**
 * API for working with payouts (admin perspective).
 */
export class AdminPayoutApi extends ApiClient {
  get(id: string): Promise<PayoutResource> {
    return this._get(`payouts/admin/v1/payouts/${id}`)
  }
}

/**
 * API for working with pending transactions.
 */
export class PendingTransactionApi extends ApiClient {
  list(
    request: ListPendingTransactionsRequest,
    signal?: AbortSignal,
  ): Promise<AipCursorPaged<PendingTransactionResource>> {
    const params = toURLSearchParams({
      page_size: request.page_size,
      page_token: request.page_token,
      order_by: request.order_by,
      search_text: request.search_text,
      flags: request.flags,
      transaction_source: request.transaction_source,
      date_created_from: request.date_created_from,
      date_created_to: request.date_created_to,
      date_transacted_from: request.date_transacted_from,
      date_transacted_to: request.date_transacted_to,
      workspace_ids: request.workspace_ids,
      recipient_user_id: request.recipient_user_id,
    })
    return this._get(`payouts/admin/v1/pending-transactions`, params, {
      signal,
    })
  }
}

/**
 * API for working with transactions (admin perspective).
 */
export class AdminTransactionApi extends ApiClient {
  listHistoricalTransactions(
    request: ListAdminHistoricalTransactionsRequest,
    signal?: AbortSignal,
  ): Promise<AipCursorPaged<AdminHistoricalTransactionResource>> {
    const params = toURLSearchParams({
      page_size: request.page_size,
      page_token: request.page_token,
      order_by: request.order_by,
      search_text: request.search_text,
      flags: request.flags,
      transaction_source: request.transaction_source,
      date_completed_from: request.date_completed_from,
      date_completed_to: request.date_completed_to,
      workspace_ids: request.workspace_ids,
      recipient_user_id: request.recipient_user_id,
    })
    return this._get('payouts/admin/v1/transactions', params, {
      signal,
    })
  }

  async downloadHistoricalTransactionsAsCsv(
    request: DownloadAdminHistoricalTransactionsAsCsvRequest,
  ): Promise<ReadableStream<Uint8Array>> {
    // Need to use `fetch` to get a stream.
    const params = new URLSearchParams({
      ...(request.search_text && { search_text: request.search_text }),
      ...(request.workspace_ids && { workspace_ids: request.workspace_ids }),
      ...(request.flags && { flags: request.flags }),
      ...(request.transaction_source && {
        transaction_source: request.transaction_source,
      }),
      ...(request.recipient_user_id && {
        recipient_user_id: request.recipient_user_id,
      }),
      ...(request.date_completed_from && {
        date_completed_from: request.date_completed_from.toISOString(),
      }),
      ...(request.date_completed_to && {
        date_completed_to: request.date_completed_to.toISOString(),
      }),
    })
    const httpRequest = new Request(
      this._createURL('payouts/admin/v1/transactions.csv', params),
      { method: 'GET' },
    )
    const response = await this._send(httpRequest).then(
      ApiClient.throwIfNotSuccessful,
    )

    return response.body!
  }

  get(id: string): Promise<AdminTransactionResource> {
    return this._get(`payouts/admin/v1/transactions/${id}`)
  }

  createUserTransaction(
    request: CreateUserTransactionRequest,
  ): Promise<CreateUserTransactionResponse> {
    return this._post(`payouts/admin/v1/transactions`, request)
  }

  cancelUserTransaction(id: string): Promise<CancelUserTransactionResponse> {
    return this._delete(`payouts/admin/v1/transactions/${id}`)
  }
}
